import type { SedeConfig } from './types'
import { THEME_BOOT_SCRIPT, THEME_HEAD_TAGS } from '../sites/verde-vercelli-2/theme/inlineScript'

/**
 * Verde Vercelli — redesigned site (slug verde-vercelli-2). Same business and
 * domain as ./verde-vercelli.ts (sedeId 1); cutover is an nginx root swap.
 * `seo` is the SPA shell's fallback head; every route gets its own head from
 * src/sites/verde-vercelli-2/head.ts, and JSON-LD comes from its jsonld.ts.
 */
export const verdeVercelli2: SedeConfig = {
  slug: 'verde-vercelli-2',
  domains: ['verdevercelli.it', 'www.verdevercelli.it'],
  sedeId: 1,
  brand: {
    name: 'Verde Vercelli',
    tagline: 'Manutenzione giardini nel Vercellese',
    primaryRgb: '1 50 32',          // #013220 (default AVMG dark green)
    primaryHoverRgb: '1 100 50',    // #016432
    inkRgb: '255 255 255',
  },
  contact: {
    email: 'info@verdevercelli.it',
    privacyEmail: 'privacy@verdevercelli.it',
    phone: '+39 348 438 4139',
    address: 'Via dei Ciudin 8, 13100 Vercelli',
    piva: '02482010028',
    addressStreet: 'Via dei Ciudin 8',
    postalCode: '13100',
  },
  founder: { name: 'Andrea Vetri', role: 'Titolare', foundedYear: 2011 },
  legal: { legalName: 'Verde Vercelli di Andrea Vetri', piva: '02482010028', foundingYear: 2011 },
  hours: [{ days: ['Mo', 'Tu', 'We', 'Th', 'Fr'], opens: '08:00', closes: '18:00' }],
  socials: { google: 'https://share.google/mA5JcEXJv0lNx2yyF' },
  // The shared legal section (src/legal/).
  legalPages: {
    locales: ['it', 'en'],
    legalForm: 'Ditta individuale',
    city: 'Vercelli (VC)',
    lastUpdated: '2026-10-16', // privacy/cookie/deletion corrected to the 2026-10-16 legal-corpus audit (§7.18)
    // Own WhatsApp channel answered by the AI assistant: every Sede has its own number (no network default).
    whatsappAi: true,
    // home Numeri.tsx fetches the Google rating (reviews/googleRating.ts → places.googleapis.com).
    googlePlaces: true,
    // The management-platform provider (AVMG), the Sede's processor (art. 28 GDPR).
    platformProvider: 'LEONARDO RICCARDO RAVOT',
    homePath: { it: '/', en: '/en/' },
    header: { logoSrc: '/media/verde-vercelli/logo.png', tagline: 'di Andrea Vetri' },
    // Shared with the site's ThemeToggle (sites/verde-vercelli-2/theme/theme.ts).
    themeStorageKey: 'verde-vercelli-theme',
    businessId: 'https://verdevercelli.it/#business',
    ogImage: '/media/verde-vercelli-2/og/{key}-{locale}.jpg',
    credits: {
      entries: [],
      notes: {
        it: [
          'Foto, video e grafica: Verde Vercelli.',
          'Caratteri tipografici: Inter nelle pagine, Fraunces e JetBrains Mono nelle anteprime social; tutti rilasciati con licenza SIL Open Font License 1.1.',
        ],
        en: [
          'Photos, videos and graphics: Verde Vercelli.',
          'Typefaces: Inter on the pages, Fraunces and JetBrains Mono in the social previews; all released under the SIL Open Font License 1.1.',
        ],
      },
    },
  },
  seo: {
    title: 'Verde Vercelli — Manutenzione Giardini e Spazi Verdi nel Vercellese',
    description: "Verde Vercelli di Andrea Vetri. Manutenzione giardini, potatura, progettazione e cura del verde per privati, condomini e aziende a Vercelli e provincia. Dal 2011.",
    keywords: 'manutenzione giardini Vercelli, giardiniere Vercelli, potatura alberi Vercelli, cura verde Vercelli, Verde Vercelli, Andrea Vetri, taglio prato, tosatura siepi, tree climbing, abbattimento alberi, progettazione giardini, pulizia giardino, fresatura ceppi',
    ogTitle: 'Verde Vercelli — Manutenzione Giardini e Spazi Verdi',
    ogImage: 'https://verdevercelli.it/media/verde-vercelli-2/og/home-it.jpg',
    ogImageAlt: 'Verde Vercelli — manutenzione giardini nel Vercellese',
    canonical: 'https://verdevercelli.it/',
    geo: {
      region: 'IT-VC',
      placename: 'Vercelli',
      lat: 45.3225,
      lon: 8.4186,
    },
    themeColor: '#013220',
    appleTitle: 'Verde Vercelli',
    googleVerification: 'jtvSPDYpfkBAODGVVv7f3qx94RtLwf5nOe3ovkWHrb8',
    // The «VV» mark (favicon.svg) stays the site icon Google shows; the rasters
    // in sede-public/verde-vercelli-2/ are the same mark for /favicon.ico
    // requests, iOS (which ignores SVG touch icons) and the manifest.
    iconLinks: [
      '<link rel="icon" href="/favicon.ico" sizes="48x48" />',
      '<link rel="icon" type="image/svg+xml" href="/favicon.svg" />',
      '<link rel="icon" type="image/png" sizes="192x192" href="/icon-192.png" />',
      '<link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />',
      '<link rel="manifest" href="/site.webmanifest" />',
    ],
    jsonLd: [],   // per-route JSON-LD comes from src/sites/verde-vercelli-2/jsonld.ts
  },
  // Theme tags BEFORE the boot script: on a saved dark choice the script
  // repaints the theme-color meta, which must already be parsed.
  extraHead: [...THEME_HEAD_TAGS, THEME_BOOT_SCRIPT],
  copy: {},
}
